import { useState } from "react";
import { StickyNote, Loader2, Plus, Share2, ShieldOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const KINDS = [
    { key: "kundenote", label: "Kundenote", tone: "bg-blue-50 border-blue-100 text-blue-800", chip: "bg-blue-100 text-blue-700" },
    { key: "opgavenote", label: "Opgavenote", tone: "bg-amber-50/70 border-amber-100 text-slate-800", chip: "bg-amber-100 text-amber-800" },
    { key: "intern", label: "Intern note", tone: "bg-slate-100 border-slate-200 text-slate-700", chip: "bg-slate-200 text-slate-700" },
];
const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[1];

// Tre notetyper gemmes separat. Deling med kunden er ALTID et eksplicit valg (aldrig automatisk).
export default function JobNotes({ jobId, notes, customerRecordNote, onNotes }) {
    const api = useEmployeeApi();
    const [text, setText] = useState("");
    const [kind, setKind] = useState("opgavenote");
    const [share, setShare] = useState(false);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState("");

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!text.trim()) return;
        setSaving(true);
        try {
            const r = await api.addTypedNote(jobId, { text: text.trim(), kind, share_with_customer: kind !== "intern" && share });
            onNotes(r.notes || []); setText(""); setShare(false);
            toast.success(`${kindMeta(kind).label} gemt`);
        } catch { toast.error("Kunne ikke gemme noten"); }
        finally { setSaving(false); }
    };
    const toggleShare = async (n) => {
        setBusyId(n.id);
        try { const r = await api.shareNote(jobId, n.id, !n.shared); onNotes(r.notes || []); toast.success(!n.shared ? "Delt med kunden" : "Deling fjernet"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke ændre deling"); }
        finally { setBusyId(""); }
    };

    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-notes">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" />Noter ({notes.length})</h2>
            {customerRecordNote && (
                <div className="mb-3 rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700" data-testid="customer-record-note">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1"><Lock className="w-3 h-3" />Fra kundekartoteket (internt)</p>
                    <p className="whitespace-pre-wrap">{customerRecordNote}</p>
                </div>
            )}
            {notes.length > 0 ? (
                <ul className="space-y-2 mb-4" data-testid="job-notes-list">
                    {notes.map((n, i) => {
                        const m = kindMeta(n.kind);
                        return (
                            <li key={n.id || i} className={`rounded-xl border px-3 py-2 text-sm ${m.tone}`} data-testid={`note-${n.kind}`}>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${m.chip}`}>{m.label}</span>
                                    {n.shared && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="note-shared-badge"><Share2 className="w-2.5 h-2.5" />Delt med kunden</span>}
                                </div>
                                <p className="whitespace-pre-wrap">{n.text}</p>
                                <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs text-slate-500">{n.author || "Medarbejder"}{n.time ? ` · ${n.time}` : ""}</p>
                                    {n.id && n.kind !== "intern" && (
                                        <button onClick={() => toggleShare(n)} disabled={busyId === n.id} data-testid="note-share-toggle" className="text-xs font-semibold text-blue-600 flex items-center gap-1 disabled:opacity-50">
                                            {busyId === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : n.shared ? <ShieldOff className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                                            {n.shared ? "Fjern deling" : "Del med kunden"}
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : <p className="text-sm text-slate-400 italic mb-4">Ingen noter endnu.</p>}

            <form onSubmit={submit} className="space-y-2">
                <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1" data-testid="note-kind-picker">
                    {KINDS.map((k) => (
                        <button key={k.key} type="button" onClick={() => { setKind(k.key); if (k.key === "intern") setShare(false); }} data-testid={`note-kind-${k.key}`}
                            className={`h-8 rounded-lg text-xs font-semibold transition-colors ${kind === k.key ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{k.label}</button>
                    ))}
                </div>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={kind === "kundenote" ? "Om kunden — fx adgangsforhold, hund…" : kind === "intern" ? "Kun for kolleger/ejer…" : "Om opgaven…"} data-testid="job-note-input"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
                <label className={`flex items-center gap-2 text-sm ${kind === "intern" ? "text-slate-300" : "text-slate-700"}`}>
                    <input type="checkbox" checked={share} disabled={kind === "intern"} onChange={(e) => setShare(e.target.checked)} data-testid="note-share-checkbox" className="w-4 h-4 rounded border-slate-300" />
                    Del med kunden i kundeportalen {kind === "intern" && "(interne noter kan ikke deles)"}
                </label>
                <button type="submit" disabled={saving || !text.trim()} data-testid="job-note-save"
                    className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Tilføj {kindMeta(kind).label.toLowerCase()}
                </button>
            </form>
        </section>
    );
}
